import React, { useState } from 'react';

// Hooks
import useCreateCustomer from '@src/hooks/useCreateCustomer';
import useCustomerLogin from '@src/hooks/useCustomerLogin';

// Comps
import { TextField } from "@src/stories/elements/TextField/TextField";
import {CustomFields} from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import ButtonSpinner from '@project-stories/elements/ButtonSpinner/ButtonSpinner';
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@project-stories/elements/Paragraph/Paragraph';
import {Checkbox} from "@project-stories/elements/Checkbox/Checkbox";
import useCustomerAccessToken from "@src/hooks/useCustomerAccessToken";

type Props = {
    section: any;
    showCustomFields?: boolean;
    signUpFields?: any;
};

const notRequiredFields = ['phone', 'optInEmail'];

const AccountRegisterForm = ({ section, showCustomFields, signUpFields }: Props) => {
    const {
        first_name_label,
        first_name_placeholder,
        last_name_label,
        last_name_placeholder,
        email_label,
        email_placeholder,
        phone_label,
        phone_placeholder,
        password_label,
        password_placeholder,
        password_confirm_label,
        password_confirm_placeholder,
        button_label_register,
        button_style_register,
        button_label_already_registered
    } = section;

    // Sort custom fields
    const customSignUpFields = signUpFields.sort((a, b) => {
        const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
        const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
        return orderA - orderB;
    });

    // Hooks
    const {
        createCustomer,
        loading: createCustomerLoading,
        error: createCustomerError,
        success: createCustomerSuccess
    } = useCreateCustomer();
    const { loginCustomer, loading: loginLoading } = useCustomerLogin();
    const {
        getAccessToken,
        loading: accessLoading,
        errorMessage: accessError,
        successMessage: accessSuccess
    } = useCustomerAccessToken();

    // States
    const initialDependenciesState = Object.assign(
        {},
        {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            password_confirmation: '',
            agreement: false
        },
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: ''
        }))
    );
    const initialDependenciesStateFilled = Object.assign(
        {},
        {
            first_name: false,
            last_name: false,
            email: false,
            password: false,
            password_confirmation: false,
            agreement: false
        },
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: field.is_required ? false : true
        }))
    );
    const initialDependenciesStateHasError = Object.assign(
        {},
        {
            first_name: false,
            last_name: false,
            email: false,
            password: false,
            password_confirmation: false,
            agreement: false
        },
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: false
        }))
    );
    const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
    const [dependenciesStateFilled, setDependenciesStateFilled]: any = useState(initialDependenciesStateFilled);
    const [dependenciesStateHasError, setDependenciesStateHasError]: any = useState(initialDependenciesStateHasError);

    // Handle input change and update global dependency state
    const handleInputChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;

        // For checkboxes, use the checked value, otherwise use the input value
        const newValue = inputType === 'checkbox' ? checked : value;

        // Update the global dependency state
        updateDependencyState(name, newValue);

        // Check if the global dependency state is filled
        toggleDependencyStateFilled(name, newValue);
    };

    // Function to update the global dependencies state
    const updateDependencyState = (name, value) => {
        setDependenciesState((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Function to check if the global dependencies state has value
    const toggleDependencyStateFilled = (name, value) => {
        if (name === 'agreement') {
            setDependenciesStateFilled((prev) => ({
                ...prev,
                agreement: !!value
            }));
            return;
        }

        const customField = customSignUpFields.find((field) => name === field.metafield_key);
        setDependenciesStateFilled((prev) => ({
            ...prev,
            [name]: customField && !customField.is_required ? true : !!value
        }));
    };

    const resetErrors = () => {
        let errors = {};

        for (const field in dependenciesStateHasError) {
            errors = {
                ...errors,
                [field]: false
            };
        }

        setDependenciesStateHasError(errors);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        resetErrors();

        // Validate the fields
        let errors = {};
        for (const field in dependenciesStateFilled) {

            if (notRequiredFields.includes(field)) {
                continue;
            }
            errors = {
                ...errors,
                [field]: !dependenciesStateFilled[field]
            };
        }

        // Agreement must be checked
        if (!dependenciesState.agreement) {
            errors = {
                ...errors,
                agreement: true
            };
        }

        for (const error in errors) {
            if (errors[error]) {
                setDependenciesStateHasError(errors);
                return;
            }
        }

        setDependenciesState((prevState) => ({
            ...prevState,
            ['optInSMS']: false,
            ['optInEmail']: false
        }));

        const createResponse = await createCustomer(dependenciesState);

        if (createResponse) {
            const email = dependenciesState.email;
            const password = dependenciesState.password;
            // Check if the pair of email and password exists in the system
            const data: any = await getAccessToken(email, password);
            // If exists, then sign in
            if (data.success) {
                // Log the customer in after successful creation
                await loginCustomer(email, password);
            }
        }
    };

    return (
        <form
            id='registerForm'
            onSubmit={handleSubmit}
            className='account-page__form max-w-[768px] mx-auto'
        >
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className={`w-full md:col-span-2 md:mb-8`}>
                    <h4 className={`w-full text-rhv-orange text-center md:text-left mb-2`}>
                        Create an account
                    </h4>

                    <p className={`b4 max-md:text-center`}>
                        Make it easier to track your orders.
                    </p>
                </div>

                {/* First Name */}
                <TextField
                    inputType='text'
                    type="floating-input"
                    id='firstNameRegister'
                    name='first_name'
                    required={true}
                    value={dependenciesState['first_name']}
                    label={`${first_name_label}*`}
                    placeholder={`${first_name_placeholder}`}
                    hasError={dependenciesStateHasError['first_name']}
                    errorMessage={`${first_name_label} is required`}
                    onChange={handleInputChange}
                />

                {/* Last Name */}
                <TextField
                    inputType='text'
                    type="floating-input" 
                    id='lastNameRegister'
                    name='last_name'
                    value={dependenciesState['last_name']}
                    onChange={handleInputChange}
                    required={true}
                    label={`${last_name_label}*`}
                    placeholder={`${last_name_placeholder}`}
                    hasError={dependenciesStateHasError['last_name']}
                    errorMessage={`${last_name_label} is required`}
                />

                {/* Email */}
                <TextField
                    inputType='email'
                    type="floating-input" 
                    id='emailRegister'
                    name='email'
                    value={dependenciesState['email']}
                    onChange={handleInputChange}
                    required={true}
                    label={`${email_label}*`}
                    placeholder={`${email_placeholder}`}
                    hasError={dependenciesStateHasError['email']}
                    errorMessage={`${email_label} is required`}
                />

                <TextField
                    inputType='tel'
                    type="floating-input" 
                    id='phoneRegister'
                    name='phone'
                    value={dependenciesState['phone']}
                    onChange={handleInputChange}
                    required={false}
                    label={`${phone_label}`}
                    placeholder={`${phone_placeholder}`}
                />

                {/* Password */}
                <TextField
                    inputType='password'
                    type="floating-input" 
                    id='passwordRegister'
                    name='password'
                    value={dependenciesState['password']}
                    onChange={handleInputChange}
                    required={true}
                    label={`${password_label}*`}
                    placeholder={`${password_placeholder}`}
                    hasError={dependenciesStateHasError['password']}
                    errorMessage={`${password_label} is required`}
                />

                {/* Confirm Password */}
                <TextField
                    inputType='password'
                    type="floating-input" 
                    id='passwordConfirmationRegister'
                    name='password_confirmation'
                    value={dependenciesState['password_confirmation']}
                    onChange={handleInputChange}
                    required={true}
                    label={`${password_confirm_label}*`}
                    placeholder={`${password_confirm_placeholder}`}
                    hasError={dependenciesStateHasError['password_confirmation']}
                    errorMessage={`${password_confirm_label} is required`}
                />


                {/* Custom Fields */}
                {showCustomFields && (
                    <CustomFields
                        customSignUpFields={customSignUpFields}
                        onChange={handleInputChange}
                        values={dependenciesState}
                        valuesError={dependenciesStateHasError}
                    />
                )}

                <div className='col-span-1'> 
                    <Checkbox id="optInEmail" label={`Sign up for newsletter`} name='optInEmail' />

                    <div className='py-4 flex flex-col gap-4'>
                        <Checkbox 
                            id="agreement" 
                            label={`I agree to the terms and privacy policy`} 
                            name="agreement"
                            onChange={handleInputChange}
                            hasError={dependenciesStateHasError['agreement']}
                        />

                        {dependenciesStateHasError['agreement'] && (
                            <div className="text-rhv-red b4 mt-1">
                                You must agree to the terms and privacy policy
                            </div>
                        )}
                    </div>

                    {createCustomerError && (
                        <p className="text-rhv-red b4 mt-1">
                            {createCustomerError}
                        </p>
                    )}

                    <ButtonSpinner
                        classes={`w-full button button-${button_style_register} min-h-[60px] !text-lg !mt-10`}
                        isLoading={createCustomerLoading || loginLoading}
                        loadingText={'Creating account...'}
                        ariaLabel='Submit registration form'
                    >
                        {button_label_register}
                    </ButtonSpinner>
                </div>

                <Link
                    link='/account/login'
                    classes='b4 link--underlined text-[#696969] hidden'
                    ariaLabel='Go to login page'
                >
                    {button_label_already_registered}
                </Link>

                {/* Social login app snippet */}
                <div className='one-click-social-login'></div>
            </div>
        </form>
    );
};

export default AccountRegisterForm;
